/**
 * Node credit quote — the ONE function that answers "how many credits will
 * this node charge for one generation?".
 *
 * Both quoting surfaces call it:
 *   - the node's own credit badge / Send button (NodeAdapters.tsx creditCost)
 *   - selectionCostUtils.getNodeCreditCost() (multi-select toolbar, whole-flow
 *     estimate)
 *
 * Before this module those were two independent re-implementations, and they
 * drifted exactly the way CLAUDE.md warns about:
 *
 *   1. The badge read `parameters.selectedModel` BEFORE the audio-aware
 *      node-type map, so a Kling node on kling-2.6 with audio ON quoted the
 *      audio-OFF SKU (140 for 5 s) while the server reserved `kling-2.6-audio`
 *      (280). Same for Kling 3.0 Standard / Pro audio.
 *   2. selectionCostUtils read `data.duration`, which no node writes — every
 *      duration setting lives in `data.parameters.duration` — so every video
 *      node quoted 5 s in the toolbar regardless of the length the user chose
 *      (a Veo node set to 8 s quoted 300 against a badge of 480).
 *
 * The rules this function follows are the handler's and the server's, not a
 * copy of them:
 *   - settings are read like the handlers' getParam(): parameters[key] ->
 *     the node definition's default -> a hardcoded fallback;
 *   - the model is read like the handlers read it: top-level
 *     `data.selectedModel` first (that is what is SENT and billed);
 *   - the fal.ai video credit SKU (`-audio`, `-t2v`) comes from the SERVER's
 *     own falVideoCreditModelId() in server/config/falModels.ts — imported, not
 *     mirrored, so the quote cannot miss a SKU split the route bills.
 *
 * Return value:
 *   - a finite number  -> the per-generation credit cost (batch NOT applied)
 *   - NaN              -> the node is priced here but has no honest number right
 *                         now (Kling Avatar before its audio is measured, a Veo
 *                         resolution/length pair the server refuses, a retired
 *                         model such as the Gemini Omni preview). Show no
 *                         quote; count it as 0 in a total.
 *   - undefined        -> this node type is not quoted here at all.
 */

import { getNodeDefinition } from '../config/nodeDefinitions';
import { getHandlerType, reactFlowTypeToType } from '../config/nodeRegistry';
import { falVideoCreditModelId } from '../../server/config/falModels';
import { isGeminiOmniModelRetired } from '../../server/config/geminiOmniRetirement';
import {
  isVeo31Model,
  resolveVeo31Resolution,
  calculateVideoCreditCost,
  calculateImageCreditCost,
  calculateWanAnimateCreditCost,
  calculateVideoScaleCreditCost,
  calculateGeminiImageCreditCost,
  calculateGPTImageCreditCost,
  calculateElevenLabsTTSCreditCost,
  resolveAudioDrivenSeconds,
  AUDIO_DRIVEN_DURATION_MODELS,
  calculateGrokImageNodeCreditCost,
  countImagesFromSources,
} from './creditCostUtils';
import { descriptorQuote } from './descriptorQuote';

type SourceNode = { type?: string; data?: Record<string, unknown> } | undefined;

export interface NodeQuoteInputs {
  /** Nodes wired INTO this one (input-image surcharges, text-vs-image-to-video). */
  sources?: SourceNode[];
  /** Length of the text a connected text input delivers (ElevenLabs TTS). */
  connectedTextCharCount?: number;
}

/**
 * Default fal.ai video model per node type — the map handleFalAIVideoGenerate
 * sends when the node carries no `data.selectedModel`. Exported so the handler
 * and the quote share it.
 */
export const FAL_VIDEO_NODE_DEFAULT_MODELS: Readonly<Record<string, string>> = {
  KlingVideo: 'kling-3.0-standard', // unified Kling node — real model comes from selectedModel
  Kling26Video: 'kling-2.6',
  Kling25Video: 'kling-2.5-turbo',
  WanVideo: 'wan-2.2', // legacy — hidden in palette, kept for back-compat
  Wan26Video: 'wan-2.7',
  LTXVideo: 'ltx-2.3',
  LTXFastVideo: 'ltx-2.3-fast',
  PixVerseVideo: 'pixverse-6',
  HunyuanVideo: 'hunyuan-1.5',
  MinimaxVideo: 'hailuo-2.3',
  KlingAvatar: 'kling-avatar',
  Seedance25Video: 'seedance-2.5',
  Seedance2Video: 'seedance-2-fast',
  MinimaxH3Video: 'minimax-h3',
};

/**
 * The fal.ai video model a node SENDS — exactly handleFalAIVideoGenerate's rule.
 *
 * `data.selectedModel` wins when it is a non-empty string. KlingO3Video ships
 * `selectedModel: ''` as a sentinel (CLAUDE.md): its model derives from the
 * `quality` setting, so an empty string must fall through to that map.
 */
export function falVideoNodeModel(
  nodeType: string,
  data: Record<string, unknown>,
): string | undefined {
  const pascal = reactFlowTypeToType(nodeType);
  if (typeof data.selectedModel === 'string' && data.selectedModel) return data.selectedModel;
  if (pascal === 'KlingO3Video') {
    const parameters = (data.parameters || {}) as Record<string, unknown>;
    return parameters.quality === 'pro' ? 'kling-3.0-pro' : 'kling-3.0-standard';
  }
  return FAL_VIDEO_NODE_DEFAULT_MODELS[pascal];
}

/**
 * The model id a fal.ai video node's generation RESERVES under on the server,
 * plus the duration/resolution the quote must price. Pure; no React.
 */
export function falVideoQuoteSpec(
  nodeType: string,
  data: Record<string, unknown>,
  inputs: NodeQuoteInputs = {},
): { model: string; creditModelId: string; duration: string; resolution: string } | undefined {
  const setting = settingReader(nodeType, data);
  const model = falVideoNodeModel(nodeType, data);
  if (!model) return undefined;

  const duration = setting('duration', '5');
  const resolution = setting('resolution', '720p');
  const enableAudio = setting('enableAudio', 'false') === 'true';
  const mode = setting('mode', 'image-to-video');

  // server/routes/falai.ts: isTextToVideo = mode === 'text-to-video' ||
  // (!imageUrl && !lastImageUrl). The images the handler can send come from
  // the same two source shapes countImagesFromSources() recognises.
  const hasImageInput = countImagesFromSources(inputs.sources ?? []) > 0;
  const isTextToVideo = mode === 'text-to-video' || !hasImageInput;

  return {
    model,
    creditModelId: falVideoCreditModelId(model, { enableAudio, isTextToVideo }),
    duration,
    resolution,
  };
}

/** getParam()-equivalent: parameters[key] -> definition default -> fallback. */
function settingReader(nodeType: string, data: Record<string, unknown>) {
  const pascal = reactFlowTypeToType(nodeType);
  const definition = getNodeDefinition(pascal);
  const parameters = (data.parameters || {}) as Record<string, unknown>;
  return (settingId: string, fallback: string): string => {
    const v = parameters[settingId];
    if (v != null && v !== '') return String(v);
    const d = definition?.settings?.find((s) => s.id === settingId)?.default;
    if (d != null) return String(d);
    return fallback;
  };
}

/**
 * Model for every non-fal-video branch. Top-level `data.selectedModel` first —
 * the handlers read it there, so it is what gets billed — then the settings
 * panel's `parameters.selectedModel`, then the definition default.
 */
function modelReader(nodeType: string, data: Record<string, unknown>) {
  const setting = settingReader(nodeType, data);
  return (fallback: string): string =>
    typeof data.selectedModel === 'string' && data.selectedModel
      ? data.selectedModel
      : setting('selectedModel', fallback);
}

const HEYGEN_NODE_MODELS: Record<string, string> = {
  HeyGenAvatarV3: 'heygen-digital-twin-v3',
  HeyGenAvatarV4: 'heygen-digital-twin-v4',
  HeyGenAvatarV: 'heygen-digital-twin-v5',
  HeyGenVideoAgent: 'heygen-video-agent',
  HeyGenTranslatePrecision: 'heygen-translate-precision',
  HeyGenTranslateSpeed: 'heygen-translate-speed',
  HeyGenPhotoVideo: 'heygen-image-to-video',
};

export const FAL_IMAGE_NODE_MODELS: Readonly<Record<string, string>> = {
  ZImageTurbo: 'z-image-turbo',
  QwenImage: 'qwen-image-2',
  QwenImageEdit: 'qwen-image-edit',
  Seedream: 'seedream-5-lite',
  RecraftV3: 'recraft-v4',
  GLMImage: 'glm-image',
  KlingImage: 'kling-image-o3',
};

const TEXT_NODE_MODELS: Record<string, string> = {
  Kimi: 'kimi-k2.6',
  Qwen: 'qwen-3.5',
  Minimax: 'minimax-m2.7',
  Claude: 'claude-haiku-4-5',
  ChatGPT: 'gpt-5.4-nano',
  GeminiText: 'gemini-3.5-flash',
  GrokText: 'grok-4.3',
};

/**
 * Credits for ONE generation of this node (batch count not applied). See the
 * module comment for the NaN / undefined contract.
 *
 * `nodeType` may be the PascalCase registry type ('KlingVideo') or the
 * React Flow type ('klingVideo').
 */
export function computeNodeBaseCredits(
  nodeType: string,
  data: Record<string, unknown>,
  inputs: NodeQuoteInputs = {},
): number | undefined {
  const pascal = reactFlowTypeToType(nodeType);
  const handler = getHandlerType(pascal);
  const setting = settingReader(pascal, data);
  const model = modelReader(pascal, data);

  // Veo 3.1 / Gemini Omni (Google direct)
  if (handler === 'video') {
    let duration = setting('duration', '8');
    const resolution = setting('resolution', '720p');
    const selectedModel = model('veo-3.1-fast-generate-preview');
    // The handler forces 8 s in first-last-frame mode, and the server bills
    // what it is sent.
    if (setting('mode', 'image-to-video') === 'first-last-frame') duration = '8';
    // A flow saved before March 2026 can still carry fal's bare 'veo-3.1' /
    // 'veo-3.1-fast' here (legacy veo3FalAI nodes). server/routes/veo.ts refuses
    // any id that is not a Veo 3.1 model, and fal's VEO 3.1 was retired in
    // migration 141, so there is no honest number to show for it. Saved flows
    // never get here with one: every load path runs migrateLegacyVeoNode
    // (server/config/falModels.ts) first, which moves them onto the Google ids.
    if (selectedModel.startsWith('veo-3.1') && !isVeo31Model(selectedModel)) return NaN;
    // Gemini Omni Flash preview: shut down by Google 2026-09-30. From that date
    // every path refuses it before reserving, so there is no price to show.
    if (isGeminiOmniModelRetired(selectedModel)) return NaN;
    // Veo 3.1: no quote for a combination the server refuses (Lite 4k,
    // 1080p/4k at a length other than 8s). Same rule as veoResolution.ts.
    if (isVeo31Model(selectedModel) && 'error' in resolveVeo31Resolution(selectedModel, resolution, duration)) {
      return NaN;
    }
    return calculateVideoCreditCost(selectedModel, duration, resolution);
  }

  if (pascal === 'WanAnimateVideo') {
    const resolution = setting('resolution', '480p');
    const seconds = typeof data.inputVideoDuration === 'number' && data.inputVideoDuration > 0
      ? data.inputVideoDuration
      : 5;
    return calculateWanAnimateCreditCost(resolution, seconds);
  }

  if (handler === 'falAIVideo') {
    const spec = falVideoQuoteSpec(pascal, data, inputs);
    if (!spec) return undefined;
    // Kling Avatar is billed by the length of the connected AUDIO. Until it is
    // measured there is no honest number.
    if (AUDIO_DRIVEN_DURATION_MODELS.has(spec.model)) {
      const seconds = resolveAudioDrivenSeconds(data.connectedAudioDuration);
      if (seconds === null) return NaN;
      return descriptorQuote(spec.creditModelId, { units: seconds, resolution: spec.resolution })
        ?? calculateVideoCreditCost(spec.creditModelId, seconds, spec.resolution);
    }
    // Descriptor-priced SKUs (DESCRIPTOR_PRICED, empty until a price flip) quote
    // priceSku() — the function the server charges with. Same axes the legacy
    // mirror receives, so the model, the '' sentinel and the quality map are
    // unchanged.
    return descriptorQuote(spec.creditModelId, { units: spec.duration, resolution: spec.resolution })
      ?? calculateVideoCreditCost(spec.creditModelId, spec.duration, spec.resolution);
  }

  // Grok Video (xAI direct). 'grok-video' is the pricing alias of the
  // standard model; the 1.5-preview id is priced directly.
  if (handler === 'xaiVideo') {
    const duration = setting('duration', '6');
    const resolution = setting('resolution', '720p');
    const selectedModel = model('grok-imagine-video');
    const pricingId = selectedModel === 'grok-imagine-video-1.5-preview'
      ? 'grok-imagine-video-1.5-preview'
      : 'grok-video';
    return calculateVideoCreditCost(pricingId, duration, resolution);
  }

  if (HEYGEN_NODE_MODELS[pascal]) {
    return calculateVideoCreditCost(model(HEYGEN_NODE_MODELS[pascal]), setting('duration', '30'), '720p');
  }
  // HeyGen Lipsync: model lives in the lipsyncVariant parameter.
  if (pascal === 'HeyGenLipsync') {
    return calculateVideoCreditCost(setting('lipsyncVariant', 'heygen-lipsync-speed'), setting('duration', '30'), '720p');
  }

  if (pascal === 'ImagePrompt') {
    // Gemini: per-(model, resolution) pricing, not the ladder.
    return calculateGeminiImageCreditCost(model('gemini-3.1-flash-image'), setting('resolution', '1024px'));
  }

  if (pascal === 'GPTImage') {
    const selectedModel = model('gpt-image-2');
    const defaultQuality = selectedModel === 'gpt-image-1-mini' ? 'low' : 'medium';
    return calculateGPTImageCreditCost(selectedModel, setting('quality', defaultQuality), setting('resolution', '1024px'));
  }

  if (pascal === 'FluxImage') {
    const fluxModel = model('flux-2-flash');
    return descriptorQuote(fluxModel, { units: 1, resolution: '720p' })
      ?? calculateImageCreditCost(fluxModel, '720p');
  }

  if (pascal === 'GrokImage') {
    // Grok Image 2.0 is priced per (quality x resolution) + input images; the
    // older models are flat.
    return calculateGrokImageNodeCreditCost(
      model('grok-image'),
      { quality: setting('quality', 'low'), resolution: setting('resolution', '1k') },
      countImagesFromSources(inputs.sources ?? []),
    );
  }

  if (handler === 'falAIImage') {
    const falModel = typeof data.selectedModel === 'string' && data.selectedModel
      ? data.selectedModel
      : FAL_IMAGE_NODE_MODELS[pascal] || 'z-image-turbo';
    // Kling Image is the one fal.ai image model with resolution-dependent
    // pricing (1K == 2K, 4K doubles). Every other one is flat per generation.
    if (falModel === 'kling-image-o3') {
      const resolution = setting('resolution', '1K');
      return descriptorQuote(falModel, { units: 1, resolution })
        ?? calculateImageCreditCost(falModel, resolution);
    }
    return descriptorQuote(falModel, { units: 1, resolution: '720p' })
      ?? calculateImageCreditCost(falModel, '720p');
  }

  if (TEXT_NODE_MODELS[pascal]) {
    const selectedModel = typeof data.selectedModel === 'string' && data.selectedModel
      ? data.selectedModel
      : TEXT_NODE_MODELS[pascal];
    return calculateImageCreditCost(selectedModel, '720p');
  }

  // ImageScale: only AI Enhance costs credits.
  if (pascal === 'ImageScale') {
    return setting('mode', 'resize') === 'ai-enhance' ? calculateImageCreditCost('esrgan', '720p') : 0;
  }

  // VideoScale: only AI Enhance costs credits (per second x resolution).
  if (pascal === 'VideoScale') {
    if (setting('mode', 'resize') !== 'ai-enhance') return 0;
    const seconds = typeof data.inputVideoDuration === 'number' && data.inputVideoDuration > 0
      ? data.inputVideoDuration
      : 5;
    return calculateVideoScaleCreditCost(setting('targetResolution', '1080p'), seconds);
  }

  // ElevenLabs audio (TTS char-based, SFX / Music per generation).
  if (handler === 'audioGen') {
    if (pascal === 'SoundEffects') return calculateElevenLabsTTSCreditCost('eleven_text_to_sound_v2', 0);
    if (pascal === 'Music') return calculateElevenLabsTTSCreditCost(model('music_v2'), 0);
    // VoiceTTS — quote the SAME text the handler sends: an explicit prompt
    // wins, otherwise the connected text input.
    const prompt = typeof data.prompt === 'string' ? data.prompt : '';
    const charCount = prompt ? prompt.length : (inputs.connectedTextCharCount ?? 0);
    return calculateElevenLabsTTSCreditCost(model('eleven_v3'), charCount);
  }

  return undefined;
}
